import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { ApiError } from '@/types/api.types';
import { registerWithGoogleInvite } from '@features/auth/api/authApi';
import { useAuthStore } from '@features/auth/store/authStore';
import type { AuthTokens, GoogleUser, User } from '@features/auth/types/auth.types';

const INVITE_REGISTER_ERROR_MAP: Partial<Record<string, string>> = {
  UNAUTHORIZED: 'inviteAccept.errors.googleInvalidToken',
  INVITATION_NOT_FOUND: 'inviteAccept.errors.invitationNotFound',
  VALIDATION_ERROR: 'inviteAccept.errors.generic',
  INTERNAL_SERVER_ERROR: 'inviteAccept.errors.generic',
};

function mapGoogleUser(googleUser: GoogleUser): User {
  return {
    id: googleUser.cognitoSub,
    email: googleUser.email,
    firstName: googleUser.firstName,
    lastName: googleUser.lastName,
    role: 'user',
    avatarUrl: googleUser.profilePicture,
    createdAt: new Date().toISOString(),
  };
}

export function useInviteGoogleRegister(invitationToken: string) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setUser, setTokens, setActiveAccount } = useAuthStore();

  const mutation = useMutation({
    mutationFn: (googleIdToken: string) =>
      registerWithGoogleInvite(invitationToken, googleIdToken),
    onSuccess: (response) => {
      const { tokens, user, account } = response.data;
      const mappedUser = mapGoogleUser(user);
      const authTokens: AuthTokens = {
        idToken: tokens.idToken,
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        expiresIn: 3600,
      };
      setUser(mappedUser);
      setTokens(authTokens);
      setActiveAccount({
        accountId: account.id,
        status: 'ACTIVE',
        businessName: '',
        roles: account.roles,
      });
      navigate('/account', { replace: true });
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    const apiError = mutation.error as ApiError;
    const i18nKey = INVITE_REGISTER_ERROR_MAP[apiError.code] ?? 'inviteAccept.errors.generic';
    return t(i18nKey);
  })();

  return {
    register: mutation.mutate,
    isPending: mutation.isPending,
    errorMessage,
  };
}
